// ===================================================================
// DJSEM Live Event Protocol (v0.9). Normalized envelope + event types + payloads.
// The React UI / conductor NEVER consume raw beat-link objects — a bridge maps them
// into this canonical contract (§4/§11/§12). Framework-free, pure types + guards.
// ===================================================================

/** Current version of the normalized DJSEM live event protocol envelope. */
export const LIVE_PROTOCOL_VERSION = "0.9.0" as const;

/** The set of normalized live event kinds a bridge may emit inside a {@link DJSEMLiveEventEnvelope}. */
export type DJSEMLiveEventType =
  | "BRIDGE_READY" | "BRIDGE_HEARTBEAT" | "BRIDGE_ERROR"
  | "DEVICE_DISCOVERED" | "DEVICE_LOST" | "DEVICE_STATUS"
  | "DECK_DISCOVERED" | "DECK_LOST"
  | "TRACK_LOADED" | "TRACK_UNLOADED"
  | "PLAYBACK_STATE" | "BEAT" | "BAR"
  | "TEMPO_MASTER_CHANGED" | "PHRASE_POSITION" | "LOOP_STATE"
  | "CUE_JUMP" | "SEEK" | "PLAY_STATE_CHANGED" | "PITCH_CHANGED"
  | "SYNC_STATE_CHANGED" | "MEDIA_CHANGED" | "CONNECTION_QUALITY"
  | "SIMULATED_EVENT";

/** The bridge implementation that produced a live event. */
export type DJSEMLiveProvider =
  | "beat_link" | "open_beat_control" | "dotnet_service" | "demo_simulator" | "manual";

/** Identifies the bridge/provider/device/deck that originated a live event. */
export interface DJSEMLiveEventSource {
  provider: DJSEMLiveProvider;
  bridgeId: string;
  bridgeVersion: string;
  deviceId?: string;
  deckId?: string;
}

/** The canonical normalized envelope wrapping every live event; the only shape the UI/conductor consume. */
export interface DJSEMLiveEventEnvelope<TPayload = unknown> {
  protocolVersion: "0.9.0";
  eventId: string;
  sequence: number;
  type: DJSEMLiveEventType;
  timestamp: number;
  source: DJSEMLiveEventSource;
  payload: TPayload;
}

/** Features a connected bridge advertises support for. */
export type DJSEMBridgeCapability =
  | "device_discovery" | "beat_events" | "player_status" | "tempo_master"
  | "track_metadata" | "loop_state" | "cue_points" | "media_metadata" | "simulation";

/** Payload of a `BRIDGE_READY` event: bridge startup info and advertised capabilities. */
export interface BridgeReadyPayload { startedAt: number; bridgeVersion: string; beatLinkVersion?: string; hostName?: string; networkInterface?: string; capabilities: DJSEMBridgeCapability[]; }
/** Payload of a `BRIDGE_HEARTBEAT` event: periodic bridge liveness/summary info. */
export interface BridgeHeartbeatPayload { uptimeMs: number; deviceCount: number; deckCount: number; activeDeckId?: string; tempoMasterDeckId?: string; eventRatePerSecond: number; warnings: string[]; }
/** Payload of a `BRIDGE_ERROR` event. */
export interface BridgeErrorPayload { code: string; message: string; severity: "info" | "warning" | "error" | "fatal"; recoverable: boolean; metadata?: Record<string, unknown>; }
/** Hardware device category reported by the bridge. */
export type DJSEMDeviceType = "cdj" | "xdj" | "djm" | "rekordbox" | "unknown";
/** Payload of a `DEVICE_DISCOVERED` event. */
export interface DeviceDiscoveredPayload { deviceId: string; playerNumber?: number; name?: string; ipAddress?: string; macAddress?: string; deviceType: DJSEMDeviceType; supportsFullDjLink?: boolean; }
/** Payload of a `DEVICE_LOST` event. */
export interface DeviceLostPayload { deviceId: string; playerNumber?: number; reason?: "timeout" | "network_lost" | "device_shutdown" | "unknown"; }
/** Identity/metadata hints for a track, used both for package matching and event payloads. */
export interface TrackIdentityHints { liveTrackId?: string; rekordboxId?: string; title?: string; artist?: string; album?: string; remixName?: string; durationSeconds?: number; bpm?: number; key?: string; rating?: number; sourceSlot?: string; sourceMediaId?: string; sourceFilename?: string; }
/** Payload of a `TRACK_LOADED` event. */
export interface TrackLoadedPayload { deckId: string; playerNumber?: number; trackIdentity: TrackIdentityHints; matchingHints: TrackIdentityHints; confidence: number; }
/** Payload of a `PLAYBACK_STATE` event. */
export interface PlaybackStatePayload { deckId: string; playerNumber?: number; isPlaying: boolean; isPaused?: boolean; isCued?: boolean; positionSeconds: number; positionBeats?: number; barNumber?: number; beatInBar?: number; bpm?: number; effectiveBpm?: number; pitchPercent?: number; tempoMaster?: boolean; synced?: boolean; updatedAt: number; confidence: number; }
/** Payload of a `BEAT` event. */
export interface BeatPayload { deckId: string; playerNumber?: number; beatNumber?: number; beatInBar?: number; barNumber?: number; bpm?: number; effectiveBpm?: number; positionSeconds?: number; isTempoMaster?: boolean; strength?: number; receivedAt: number; }
/** Payload of a `BAR` event. */
export interface BarPayload { deckId: string; playerNumber?: number; barNumber?: number; bpm?: number; positionSeconds?: number; receivedAt: number; }
/** Payload of a `LOOP_STATE` event. */
export interface LoopStatePayload { deckId: string; isLooping: boolean; loopStartSeconds?: number; loopEndSeconds?: number; loopLengthBeats?: number; confidence: number; }
/** Payload of a `CUE_JUMP` / `SEEK` event. */
export interface CueJumpPayload { deckId: string; fromSeconds?: number; toSeconds: number; jumpType: "hot_cue" | "memory_cue" | "manual_seek" | "loop_exit" | "unknown"; confidence: number; }
/** Payload of a `TEMPO_MASTER_CHANGED` event. */
export interface TempoMasterChangedPayload { previousDeckId?: string; newDeckId?: string; playerNumber?: number; bpm?: number; confidence: number; }
/** Payload of a `TRACK_UNLOADED` event. */
export interface TrackUnloadedPayload { deckId: string; }

const KNOWN_TYPES = new Set<DJSEMLiveEventType>([
  "BRIDGE_READY","BRIDGE_HEARTBEAT","BRIDGE_ERROR","DEVICE_DISCOVERED","DEVICE_LOST","DEVICE_STATUS",
  "DECK_DISCOVERED","DECK_LOST","TRACK_LOADED","TRACK_UNLOADED","PLAYBACK_STATE","BEAT","BAR",
  "TEMPO_MASTER_CHANGED","PHRASE_POSITION","LOOP_STATE","CUE_JUMP","SEEK","PLAY_STATE_CHANGED",
  "PITCH_CHANGED","SYNC_STATE_CHANGED","MEDIA_CHANGED","CONNECTION_QUALITY","SIMULATED_EVENT",
]);

/** Result of {@link validateEnvelope}. */
export interface EnvelopeValidation { valid: boolean; known: boolean; reason?: string; }
/**
 * Validates an incoming envelope's structure. Unknown event types are tolerated
 * (surfaced via `known: false`, not fatal); structurally malformed envelopes (missing
 * required fields, wrong protocol version) are rejected so callers can safely ignore them.
 * @param e - the candidate envelope, of unknown/untrusted shape.
 * @returns whether the envelope is structurally valid and whether its type is recognized.
 */
export function validateEnvelope(e: unknown): EnvelopeValidation {
  if (typeof e !== "object" || e === null) return { valid: false, known: false, reason: "not an object" };
  const env = e as Partial<DJSEMLiveEventEnvelope>;
  if (env.protocolVersion !== LIVE_PROTOCOL_VERSION) return { valid: false, known: false, reason: "bad protocolVersion" };
  if (typeof env.eventId !== "string") return { valid: false, known: false, reason: "missing eventId" };
  if (typeof env.sequence !== "number") return { valid: false, known: false, reason: "missing sequence" };
  if (typeof env.timestamp !== "number") return { valid: false, known: false, reason: "missing timestamp" };
  if (typeof env.type !== "string") return { valid: false, known: false, reason: "missing type" };
  if (typeof env.source !== "object" || env.source === null) return { valid: false, known: false, reason: "missing source" };
  return { valid: true, known: KNOWN_TYPES.has(env.type as DJSEMLiveEventType) };
}

/**
 * Type-guards a string as a recognized {@link DJSEMLiveEventType}.
 * @param t - the candidate event type string.
 * @returns true if `t` is one of the known live event types.
 */
export function isKnownEventType(t: string): t is DJSEMLiveEventType {
  return KNOWN_TYPES.has(t as DJSEMLiveEventType);
}
